import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

export interface Slide {
  id: string | number;
  image: string;
  redirectUrl?: string;
}

interface HomeCarouselProps {
  slides: Slide[];
  isLoading?: boolean;
  autoPlay?: boolean;
  interval?: number;
  loop?: boolean;
}

export function HomeCarousel({
  slides,
  isLoading = false,
  autoPlay = true,
  interval = 6000, //carousel interval
  loop = true,
}: HomeCarouselProps) {
  const autoplayOptions = {
    delay: interval,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop, dragFree: false },
    autoPlay ? [Autoplay(autoplayOptions)] : []
  );
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const [progress, setProgress] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setProgress(0);
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Animated progress bar for autoplay timing
  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;
    const step = 50;
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + (step / interval) * 100;
      });
    }, step);
    return () => clearInterval(timer);
  }, [selectedIndex, autoPlay, interval, slides.length]);

  if (isLoading) {
    return (
      <section className="px-4 mb-4 mt-4" role="region" aria-label="Loading Carousel">
        <Skeleton className="w-full aspect-[21/9] rounded-sm" />
      </section>
    );
  }

  if (!slides || slides.length === 0) return null;

  return (
    <section
      className="px-3 mb-4 mt-11"
      role="region"
      aria-label="Promotional Carousel"
    >
      {/* Inline keyframes for shimmer */}
      <style>{`
        @keyframes shimmerSweep {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(200%) skewX(-15deg); }
        }
      `}</style>

      <div className="relative group">
        {/* Main Carousel */}
        <div
          className="relative rounded-lg overflow-hidden aspect-[21/9] bg-white transform transition-transform duration-500 hover:scale-[1.03] scale-[1.02] z-10"
          ref={emblaRef}
          style={{
           // boxShadow: 'inset 0 1px 0 rgba(255,255,255,1), 0 20px 40px -10px rgba(255,255,255,0.25), 0 10px 15px -5px rgba(255, 255, 255, 1)',
          }}
        >
          <div className="flex h-full w-full">
            {slides.map((slide, i) => {
              const isActive = i === selectedIndex;
              return (
                <div
                  key={slide.id}
                  className="relative flex-[0_0_100%] min-w-0 h-full w-full overflow-hidden"
                >
                  {slide.redirectUrl ? (
                    <Link to={slide.redirectUrl} className="block w-full h-full">
                      <img
                        src={slide.image}
                        alt="Promotional Banner"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </Link>
                  ) : (
                    <img
                      src={slide.image}
                      alt="Promotional Banner"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}

                  {/* Shimmer sweep on active slide */}
                  {isActive && (
                    <div
                      className="absolute inset-0 pointer-events-none overflow-hidden"
                      aria-hidden="true"
                    >
                      <div
                        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        style={{
                          animation: `shimmerSweep ${interval * 0.6}ms ease-in-out 0.3s 1 forwards`,
                        }}
                      />
                    </div>
                  )}

                  {/* Bottom gradient for dot visibility */}
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />
                </div>
              );
            })}
          </div>

          {/* Inline Pagination Dots */}
          {scrollSnaps.length > 1 && (
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={cn(
                    "rounded-full transition-all duration-500 ease-out",
                    index === selectedIndex
                      ? "w-5 h-1.5 bg-white shadow-[0_0_6px_rgba(255,255,255,0.5)]"
                      : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
                  )}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Autoplay progress bar */}
          {autoPlay && scrollSnaps.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 z-10">
              <div
                className="h-full bg-white/60 rounded-r-full"
                style={{
                  width: `${progress}%`,
                  transition: 'width 50ms linear',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
